import { useEffect, useRef } from "react";

interface BoundaryCanvasProps {
  fromAbyss?: boolean;
  timeLeft?: number; // 残り秒数（60〜0）
  countdownMax?: number;
}

export default function BoundaryCanvas({
  fromAbyss = false,
  timeLeft = 60,
  countdownMax = 60,
}: BoundaryCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  // timeLeft を ref で持ち、draw ループ内でリアルタイム参照
  const timeLeftRef = useRef(timeLeft);
  timeLeftRef.current = timeLeft;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const cx = () => canvas.width / 2;
    const cy = () => canvas.height / 2;

    // ── 天界の光の柱 ──
    interface LightPillar {
      x: number;
      width: number;
      opacity: number;
      speed: number;
      phase: number;
    }
    const pillars: LightPillar[] = Array.from({ length: 8 }, (_, i) => ({
      x: (i / 8) * canvas.width + Math.random() * (canvas.width / 8),
      width: 20 + Math.random() * 60,
      opacity: 0.02 + Math.random() * 0.06,
      speed: 0.3 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2,
    }));

    // ── 天界の星粒子 ──
    interface StarMote {
      x: number; y: number;
      vy: number;
      opacity: number;
      size: number;
      twinkle: number;
    }
    const stars: StarMote[] = Array.from({ length: 120 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * ((canvas.height || 1080) * 0.5),
      vy: 0.05 + Math.random() * 0.15,
      opacity: 0.1 + Math.random() * 0.5,
      size: 0.3 + Math.random() * 1.2,
      twinkle: Math.random() * Math.PI * 2,
    }));

    // ── 地球の霧粒子 ──
    interface FogMote {
      x: number; y: number;
      vx: number; vy: number;
      opacity: number;
      size: number;
      phase: number;
    }
    const fogs: FogMote[] = Array.from({ length: 60 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: (canvas.height || 1080) * 0.5 + Math.random() * (canvas.height || 1080) * 0.5,
      vx: (Math.random() - 0.5) * 0.3,
      vy: -(0.1 + Math.random() * 0.2),
      opacity: 0.03 + Math.random() * 0.08,
      size: 40 + Math.random() * 120,
      phase: Math.random() * Math.PI * 2,
    }));

    // ── 境界線の光の粒子 ──
    interface BoundaryMote {
      x: number;
      vx: number;
      opacity: number;
      size: number;
      phase: number;
    }
    const boundaryMotes: BoundaryMote[] = Array.from({ length: 80 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      vx: (Math.random() - 0.5) * 0.8,
      opacity: 0.2 + Math.random() * 0.6,
      size: 0.5 + Math.random() * 2,
      phase: Math.random() * Math.PI * 2,
    }));

    // ── 天界から降り注ぐ光の雨 ──
    interface LightRain {
      x: number; y: number;
      vy: number;
      opacity: number;
      length: number;
    }
    const lightRains: LightRain[] = Array.from({ length: 30 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 1080) * 0.5,
      vy: 0.8 + Math.random() * 1.5,
      opacity: 0.05 + Math.random() * 0.12,
      length: 20 + Math.random() * 60,
    }));

    let t = 0;

    function draw() {
      t++;
      const w = canvas.width;
      const h = canvas.height;
      const midY = h * 0.5;

      // 残り時間に応じた赤の侵食度（0〜1）
      const tl = timeLeftRef.current;
      const redIntensity = tl <= 35
        ? Math.pow(1 - tl / 35, 1.4)  // 35秒以下から徐々に赤く
        : 0;
      // 10秒以下は脈打つ
      const redPulse = tl <= 10
        ? (Math.sin(t * 0.18) * 0.15 + 0.85)
        : 1;
      const ri = redIntensity * redPulse;

      ctx.clearRect(0, 0, w, h);

      // ══════════════════════════════════════════
      // 天界（上半分）— 残り時間で赤く染まる
      // ══════════════════════════════════════════
      const heavenGrd = ctx.createLinearGradient(0, 0, 0, midY);
      if (fromAbyss) {
        heavenGrd.addColorStop(0, "rgba(5, 0, 15, 1)");
        heavenGrd.addColorStop(0.6, "rgba(15, 5, 30, 1)");
        heavenGrd.addColorStop(1, "rgba(30, 10, 50, 1)");
      } else {
        // 通常は深い紺→残り時間で血の赤に
        const r0 = Math.round(2 + ri * 30);
        const g0 = Math.round(2 - ri * 2);
        const b0 = Math.round(12 - ri * 10);
        const r1 = Math.round(5 + ri * 40);
        const g1 = Math.round(5 - ri * 4);
        const b1 = Math.round(20 - ri * 18);
        const r2 = Math.round(10 + ri * 50);
        const g2 = Math.round(8 - ri * 6);
        const b2 = Math.round(30 - ri * 26);
        heavenGrd.addColorStop(0, `rgba(${r0}, ${g0}, ${b0}, 1)`);
        heavenGrd.addColorStop(0.5, `rgba(${r1}, ${g1}, ${b1}, 1)`);
        heavenGrd.addColorStop(1, `rgba(${r2}, ${g2}, ${b2}, 1)`);
      }
      ctx.fillStyle = heavenGrd;
      ctx.fillRect(0, 0, w, midY);

      // ══════════════════════════════════════════
      // 地球（下半分）— 残り時間で赤く染まる
      // ══════════════════════════════════════════
      const earthGrd = ctx.createLinearGradient(0, midY, 0, h);
      if (fromAbyss) {
        earthGrd.addColorStop(0, "rgba(20, 5, 5, 1)");
        earthGrd.addColorStop(0.4, "rgba(10, 3, 3, 1)");
        earthGrd.addColorStop(1, "rgba(3, 1, 1, 1)");
      } else {
        const er0 = Math.round(8 + ri * 55);
        const eg0 = Math.round(6 - ri * 5);
        const eb0 = Math.round(18 - ri * 16);
        const er1 = Math.round(5 + ri * 35);
        const eg1 = Math.round(4 - ri * 3);
        const eb1 = Math.round(12 - ri * 10);
        const er2 = Math.round(2 + ri * 15);
        const eg2 = Math.round(2 - ri * 1);
        const eb2 = Math.round(6 - ri * 4);
        earthGrd.addColorStop(0, `rgba(${er0}, ${eg0}, ${eb0}, 1)`);
        earthGrd.addColorStop(0.4, `rgba(${er1}, ${eg1}, ${eb1}, 1)`);
        earthGrd.addColorStop(1, `rgba(${er2}, ${eg2}, ${eb2}, 1)`);
      }
      ctx.fillStyle = earthGrd;
      ctx.fillRect(0, midY, w, h - midY);

      // 残り時間が少ないほど四隅から赤い霧が迫る
      if (ri > 0 && !fromAbyss) {
        const cornerIntensity = ri * 0.35;
        const cornerSize = w * 0.6 * ri;
        // 四隅
        const corners = [
          { x: 0, y: 0 },
          { x: w, y: 0 },
          { x: 0, y: h },
          { x: w, y: h },
        ];
        corners.forEach(({ x, y }) => {
          const cGrd = ctx.createRadialGradient(x, y, 0, x, y, cornerSize);
          cGrd.addColorStop(0, `rgba(180, 0, 0, ${cornerIntensity})`);
          cGrd.addColorStop(0.5, `rgba(120, 0, 0, ${cornerIntensity * 0.4})`);
          cGrd.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = cGrd;
          ctx.fillRect(
            x === 0 ? 0 : x - cornerSize,
            y === 0 ? 0 : y - cornerSize,
            cornerSize,
            cornerSize
          );
        });

        // 10秒以下：中央にも赤いグロー
        if (tl <= 10) {
          const centerPulse = Math.sin(t * 0.2) * 0.5 + 0.5;
          const centerGrd = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w * 0.5);
          centerGrd.addColorStop(0, `rgba(200, 0, 0, ${0.08 * centerPulse})`);
          centerGrd.addColorStop(0.4, `rgba(150, 0, 0, ${0.04 * centerPulse})`);
          centerGrd.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = centerGrd;
          ctx.fillRect(0, 0, w, h);
        }
      }

      // ══════════════════════════════════════════
      // 天界の光の柱
      // ══════════════════════════════════════════
      pillars.forEach((p) => {
        const pulse = Math.sin(t * 0.008 * p.speed + p.phase) * 0.5 + 0.5;
        const alpha = p.opacity * (0.5 + pulse * 0.5);
        const pillarGrd = ctx.createLinearGradient(0, 0, 0, midY);
        if (fromAbyss) {
          pillarGrd.addColorStop(0, `rgba(120, 0, 180, 0)`);
          pillarGrd.addColorStop(0.5, `rgba(150, 20, 200, ${alpha})`);
          pillarGrd.addColorStop(1, `rgba(180, 60, 220, ${alpha * 0.3})`);
        } else {
          pillarGrd.addColorStop(0, `rgba(201, 168, 76, 0)`);
          pillarGrd.addColorStop(0.5, `rgba(220, 190, 100, ${alpha})`);
          pillarGrd.addColorStop(1, `rgba(255, 220, 140, ${alpha * 0.3})`);
        }
        ctx.fillStyle = pillarGrd;
        ctx.fillRect(p.x - p.width / 2, 0, p.width, midY);
      });

      // ══════════════════════════════════════════
      // 天界の星
      // ══════════════════════════════════════════
      stars.forEach((s) => {
        s.twinkle += 0.02;
        const twinkleAlpha = s.opacity * (0.5 + Math.sin(s.twinkle) * 0.5);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        if (fromAbyss) {
          ctx.fillStyle = `rgba(200, 150, 255, ${twinkleAlpha})`;
        } else {
          ctx.fillStyle = `rgba(255, 240, 200, ${twinkleAlpha})`;
        }
        ctx.fill();

        // 明るい星には十字の輝き
        if (s.opacity > 0.4) {
          const crossLen = s.size * 4;
          const crossAlpha = twinkleAlpha * 0.4;
          ctx.beginPath();
          ctx.moveTo(s.x - crossLen, s.y);
          ctx.lineTo(s.x + crossLen, s.y);
          ctx.strokeStyle = fromAbyss
            ? `rgba(200, 150, 255, ${crossAlpha})`
            : `rgba(255, 240, 200, ${crossAlpha})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(s.x, s.y - crossLen);
          ctx.lineTo(s.x, s.y + crossLen);
          ctx.stroke();
        }
      });

      // ══════════════════════════════════════════
      // 天界から降り注ぐ光の雨
      // ══════════════════════════════════════════
      lightRains.forEach((r) => {
        r.y += r.vy;
        if (r.y > midY) {
          r.y = -r.length;
          r.x = Math.random() * w;
        }
        const rainGrd = ctx.createLinearGradient(r.x, r.y, r.x, r.y + r.length);
        rainGrd.addColorStop(0, "rgba(255,255,255,0)");
        rainGrd.addColorStop(0.5, fromAbyss
          ? `rgba(180, 100, 255, ${r.opacity})`
          : `rgba(255, 220, 140, ${r.opacity})`);
        rainGrd.addColorStop(1, "rgba(255,255,255,0)");
        ctx.beginPath();
        ctx.moveTo(r.x, r.y);
        ctx.lineTo(r.x, r.y + r.length);
        ctx.strokeStyle = rainGrd;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });

      // ══════════════════════════════════════════
      // 地球の霧
      // ══════════════════════════════════════════
      fogs.forEach((f) => {
        f.x += f.vx;
        f.y += f.vy;
        f.phase += 0.005;
        const fogAlpha = f.opacity * (0.6 + Math.sin(f.phase) * 0.4);
        if (f.y < midY * 0.8) {
          f.y = h * 0.5 + Math.random() * h * 0.4;
          f.x = Math.random() * w;
        }
        if (f.x < -f.size) f.x = w + f.size;
        if (f.x > w + f.size) f.x = -f.size;

        const fogGrd = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.size);
        fogGrd.addColorStop(0, fromAbyss
          ? `rgba(80, 10, 10, ${fogAlpha})`
          : `rgba(30, 20, 60, ${fogAlpha})`);
        fogGrd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = fogGrd;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // ══════════════════════════════════════════
      // 境界線（中央の光の地平線）
      // ══════════════════════════════════════════
      // メインの境界グロー
      const boundaryGrd = ctx.createLinearGradient(0, midY - 40, 0, midY + 40);
      if (fromAbyss) {
        boundaryGrd.addColorStop(0, "rgba(0,0,0,0)");
        boundaryGrd.addColorStop(0.4, "rgba(150, 30, 30, 0.08)");
        boundaryGrd.addColorStop(0.5, "rgba(200, 60, 60, 0.15)");
        boundaryGrd.addColorStop(0.6, "rgba(150, 30, 30, 0.08)");
        boundaryGrd.addColorStop(1, "rgba(0,0,0,0)");
      } else {
        boundaryGrd.addColorStop(0, "rgba(0,0,0,0)");
        boundaryGrd.addColorStop(0.4, "rgba(201, 168, 76, 0.06)");
        boundaryGrd.addColorStop(0.5, "rgba(255, 220, 120, 0.12)");
        boundaryGrd.addColorStop(0.6, "rgba(201, 168, 76, 0.06)");
        boundaryGrd.addColorStop(1, "rgba(0,0,0,0)");
      }
      ctx.fillStyle = boundaryGrd;
      ctx.fillRect(0, midY - 40, w, 80);

      // 境界線本体（細い光の線）
      const lineGrd = ctx.createLinearGradient(0, midY, w, midY);
      lineGrd.addColorStop(0, "rgba(255,255,255,0)");
      lineGrd.addColorStop(0.1, fromAbyss ? "rgba(200,60,60,0.15)" : "rgba(201,168,76,0.15)");
      lineGrd.addColorStop(0.5, fromAbyss ? "rgba(220,80,80,0.4)" : "rgba(255,220,120,0.45)");
      lineGrd.addColorStop(0.9, fromAbyss ? "rgba(200,60,60,0.15)" : "rgba(201,168,76,0.15)");
      lineGrd.addColorStop(1, "rgba(255,255,255,0)");
      ctx.beginPath();
      ctx.moveTo(0, midY);
      ctx.lineTo(w, midY);
      ctx.strokeStyle = lineGrd;
      ctx.lineWidth = 1;
      ctx.stroke();

      // 境界線の脈打つグロー
      const glowPulse = Math.sin(t * 0.015) * 0.5 + 0.5;
      const glowGrd = ctx.createLinearGradient(0, midY - 20, 0, midY + 20);
      glowGrd.addColorStop(0, "rgba(0,0,0,0)");
      glowGrd.addColorStop(0.5, fromAbyss
        ? `rgba(200, 60, 60, ${0.04 + glowPulse * 0.06})`
        : `rgba(255, 220, 120, ${0.04 + glowPulse * 0.08})`);
      glowGrd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = glowGrd;
      ctx.fillRect(0, midY - 20, w, 40);

      // ══════════════════════════════════════════
      // 境界線上の光の粒子
      // ══════════════════════════════════════════
      boundaryMotes.forEach((m) => {
        m.x += m.vx;
        m.phase += 0.02;
        if (m.x < 0) m.x = w;
        if (m.x > w) m.x = 0;

        const yOff = Math.sin(m.phase) * 3;
        const alpha = m.opacity * (0.4 + Math.sin(m.phase * 1.3) * 0.6);

        ctx.beginPath();
        ctx.arc(m.x, midY + yOff, m.size, 0, Math.PI * 2);
        ctx.fillStyle = fromAbyss
          ? `rgba(220, 80, 80, ${alpha})`
          : `rgba(255, 220, 120, ${alpha})`;
        ctx.fill();
      });

      // ══════════════════════════════════════════
      // 中央の宇宙的な光の収束点（境界の中心）
      // ══════════════════════════════════════════
      const convergePulse = Math.sin(t * 0.012) * 0.5 + 0.5;
      const convergeR = 80 + convergePulse * 40;
      const convergeGrd = ctx.createRadialGradient(cx(), midY, 0, cx(), midY, convergeR);
      if (fromAbyss) {
        convergeGrd.addColorStop(0, `rgba(200, 60, 60, ${0.12 + convergePulse * 0.08})`);
        convergeGrd.addColorStop(0.4, `rgba(150, 30, 30, ${0.06 + convergePulse * 0.04})`);
        convergeGrd.addColorStop(1, "rgba(0,0,0,0)");
      } else {
        convergeGrd.addColorStop(0, `rgba(255, 220, 120, ${0.1 + convergePulse * 0.08})`);
        convergeGrd.addColorStop(0.4, `rgba(201, 168, 76, ${0.05 + convergePulse * 0.04})`);
        convergeGrd.addColorStop(1, "rgba(0,0,0,0)");
      }
      ctx.fillStyle = convergeGrd;
      ctx.beginPath();
      ctx.arc(cx(), midY, convergeR, 0, Math.PI * 2);
      ctx.fill();

      // ══════════════════════════════════════════
      // 天界から地球へ伸びる光の柱（中央）
      // ══════════════════════════════════════════
      const centralPillarAlpha = 0.04 + convergePulse * 0.04;
      const centralPillarGrd = ctx.createLinearGradient(0, 0, 0, h);
      if (fromAbyss) {
        centralPillarGrd.addColorStop(0, "rgba(0,0,0,0)");
        centralPillarGrd.addColorStop(0.3, `rgba(150, 30, 30, ${centralPillarAlpha})`);
        centralPillarGrd.addColorStop(0.5, `rgba(200, 60, 60, ${centralPillarAlpha * 1.5})`);
        centralPillarGrd.addColorStop(0.7, `rgba(150, 30, 30, ${centralPillarAlpha})`);
        centralPillarGrd.addColorStop(1, "rgba(0,0,0,0)");
      } else {
        centralPillarGrd.addColorStop(0, "rgba(0,0,0,0)");
        centralPillarGrd.addColorStop(0.3, `rgba(201, 168, 76, ${centralPillarAlpha})`);
        centralPillarGrd.addColorStop(0.5, `rgba(255, 220, 120, ${centralPillarAlpha * 1.5})`);
        centralPillarGrd.addColorStop(0.7, `rgba(201, 168, 76, ${centralPillarAlpha})`);
        centralPillarGrd.addColorStop(1, "rgba(0,0,0,0)");
      }
      ctx.fillStyle = centralPillarGrd;
      ctx.fillRect(cx() - 60, 0, 120, h);

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [fromAbyss]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ width: "100vw", height: "100vh" }}
    />
  );
}
